import { useRef, useEffect, useState } from 'react'
import { motion, useInView } from 'framer-motion'
import { fadeInLeft, fadeInRight, staggerContainer, fadeInUp } from '../hooks/useScrollAnimation'

const stats = [
  { label: 'Years Experience', value: 3, suffix: '.4+' },
  { label: 'GitHub Repos', value: 92, suffix: '+' },
  { label: 'LeetCode Solved', value: 458, suffix: '' },
  { label: 'API Response Boost', value: 40, suffix: '%' },
]

const codeSnippet = `// about_seraj.ts
interface Developer {
  name: string
  role: string
  location: string
  company: string
  passion: string[]
}

const seraj: Developer = {
  name: "Seraj Khan",
  role: "Senior Software Engineer",
  location: "Bengaluru, India",
  company: "BambooBox",
  passion: [
    "AI/LLM Pipelines",
    "Microservices",
    "Backend Performance",
    "Open Source",
  ],
}

const impact = async () => {
  await improveAPILatency(by: "40%")
  await optimizeDBQueries(by: "60%")
  await maintainUptime(target: "99%")
  await shipAIFeatures(withLangChain)
}

// Currently available for:
// ✓ Full-time opportunities
// ✓ Technical consulting
// ✓ Architecture reviews`

function AnimatedCounter({ value, suffix, isInView }) {
  const [count, setCount] = useState(0)

  useEffect(() => {
    if (!isInView) return
    let start = 0
    const end = value
    const duration = 1800
    const stepTime = duration / end
    const timer = setInterval(() => {
      start += 1
      setCount(start)
      if (start >= end) clearInterval(timer)
    }, stepTime)
    return () => clearInterval(timer)
  }, [isInView, value])

  return (
    <span className="font-mono text-3xl font-bold text-[#00ff41] glow-green">
      {count}{suffix}
    </span>
  )
}

export default function About() {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: '0px 0px -100px 0px' })

  return (
    <section id="about" className="py-24 relative">
      <div className="max-w-7xl mx-auto px-6 lg:px-8" ref={ref}>
        {/* Section Header */}
        <motion.div
          variants={fadeInUp}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          className="mb-16"
        >
          <p className="font-mono text-[#00ff41] text-sm mb-2">// 01. about</p>
          <h2 className="section-title">
            About <span>Me</span>
          </h2>
          <div className="h-0.5 w-24 bg-gradient-to-r from-[#00ff41] to-transparent mt-3" />
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 xl:gap-20 items-start">
          {/* Left: Bio */}
          <motion.div
            variants={fadeInLeft}
            initial="hidden"
            animate={isInView ? 'visible' : 'hidden'}
          >
            <div className="space-y-5 text-[#8b949e] text-base leading-relaxed">
              <p>
                Hey! I'm <span className="text-[#00ff41] font-semibold">Seraj Khan</span>,
                a Senior Software Engineer with{' '}
                <span className="text-[#e6edf3]">3.4+ years of experience</span> architecting
                and delivering high-performance B2B SaaS platforms. Currently at{' '}
                <span className="text-[#e6edf3]">BambooBox</span>, where I lead end-to-end
                development of AI-powered features using Node.js, LangChain, and GPT-4o.
              </p>
              <p>
                I specialize in backend systems — building scalable microservices, optimizing
                database queries (MongoDB, PostgreSQL), and integrating CRM/Ad platforms like
                Salesforce, Zoho, Google Ads, and LinkedIn Ads. Proven impact:{' '}
                <span className="text-[#e6edf3]">40% faster APIs</span>,{' '}
                <span className="text-[#e6edf3]">60% faster DB queries</span>, and{' '}
                <span className="text-[#e6edf3]">99% uptime</span> at scale.
              </p>
              <p>
                Outside work I'm grinding LeetCode (458 problems solved), building open
                source tools, and mentoring junior developers. I believe great software
                lives at the intersection of{' '}
                <span className="text-[#00d4ff]">technical excellence</span> and{' '}
                <span className="text-[#00d4ff]">strong collaboration</span>.
              </p>
            </div>

            {/* Tech highlight row */}
            <div className="mt-8 p-4 glass-card border border-[#30363d] rounded-lg">
              <p className="font-mono text-xs text-[#6e7681] mb-3">// primary stack</p>
              <div className="flex flex-wrap gap-2">
                {[
                  'Node.js', 'TypeScript', 'LangChain', 'GPT-4o',
                  'PostgreSQL', 'MongoDB', 'Redis', 'Docker'
                ].map((tech) => (
                  <span key={tech} className="tag">{tech}</span>
                ))}
              </div>
            </div>

            {/* Stats */}
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              animate={isInView ? 'visible' : 'hidden'}
              className="grid grid-cols-2 gap-4 mt-8"
            >
              {stats.map((stat) => (
                <motion.div
                  key={stat.label}
                  variants={fadeInUp}
                  className="glass-card p-4 border border-[#30363d] text-center rounded-lg hover:border-[#00ff41]/30 transition-colors duration-300"
                >
                  <AnimatedCounter
                    value={stat.value}
                    suffix={stat.suffix}
                    isInView={isInView}
                  />
                  <p className="font-mono text-xs text-[#6e7681] mt-1">{stat.label}</p>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          {/* Right: Code Snippet */}
          <motion.div
            variants={fadeInRight}
            initial="hidden"
            animate={isInView ? 'visible' : 'hidden'}
          >
            {/* Code editor window */}
            <div className="glass-card border border-[#30363d] overflow-hidden rounded-xl">
              {/* Editor title bar */}
              <div className="flex items-center gap-2 px-4 py-3 bg-[#161b22] border-b border-[#30363d]">
                <div className="flex gap-2">
                  <div className="w-3 h-3 rounded-full bg-[#ff5f57]" />
                  <div className="w-3 h-3 rounded-full bg-[#febc2e]" />
                  <div className="w-3 h-3 rounded-full bg-[#28c840]" />
                </div>
                <div className="flex-1 text-center">
                  <span className="font-mono text-xs text-[#6e7681]">about_seraj.ts</span>
                </div>
                <div className="text-xs font-mono text-[#6e7681]">TypeScript</div>
              </div>

              {/* Code content */}
              <div className="p-5 overflow-x-auto">
                <pre className="font-mono text-xs leading-6 text-[#e6edf3]">
                  {codeSnippet.split('\n').map((line, i) => (
                    <div key={i} className="flex">
                      <span className="text-[#6e7681] select-none w-8 text-right mr-4 shrink-0 text-xs">
                        {i + 1}
                      </span>
                      <span
                        className="flex-1"
                        dangerouslySetInnerHTML={{
                          __html: line
                            .replace(/\/\/.*/g, '<span style="color:#6e7681">$&</span>')
                            .replace(/(".*?")/g, '<span style="color:#a8ff78">$1</span>')
                            .replace(/\b(const|interface|async|await|return|string)\b/g, '<span style="color:#00d4ff">$1</span>')
                            .replace(/\b(Developer|developer)\b/g, '<span style="color:#ffa657">$1</span>')
                            .replace(/✓/g, '<span style="color:#00ff41">✓</span>'),
                        }}
                      />
                    </div>
                  ))}
                </pre>
              </div>
            </div>

            {/* Available badge */}
            <motion.div
              animate={{ scale: [1, 1.02, 1] }}
              transition={{ repeat: Infinity, duration: 3 }}
              className="mt-4 inline-flex items-center gap-3 glass-card px-4 py-3 border border-[#00ff41]/30 rounded-full"
            >
              <div className="relative">
                <div className="w-2.5 h-2.5 rounded-full bg-[#00ff41]" />
                <div className="absolute inset-0 w-2.5 h-2.5 rounded-full bg-[#00ff41] animate-ping opacity-75" />
              </div>
              <span className="font-mono text-sm text-[#00ff41]">
                Available for new opportunities
              </span>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}
